import React from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { styled, useTheme } from '@mui/material/styles'
import { Collapse, CssBaseline, Divider, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, ListItemButton } from '@mui/material'
import { Group, Language, Person, Translate, Work } from '@mui/icons-material'
import { canSeeAdmin, canEditLanguages, canEditProjects, canEditUsers } from '../services/PermissionsService'

const drawerWidth = 240

const StyledDrawer = styled(Drawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    boxSizing: 'border-box',
    ...(open && {
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            boxSizing: 'border-box',
        },
    }),
    ...(!open && {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: `calc(${theme.spacing(7)} + 1px)`,
        [theme.breakpoints.up('sm')]: {
            width: `calc(${theme.spacing(9)} + 1px)`,
        },
        '& .MuiDrawer-paper': {
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            width: `calc(${theme.spacing(7)} + 1px)`,
            [theme.breakpoints.up('sm')]: {
                width: `calc(${theme.spacing(9)} + 1px)`,
            },
            boxSizing: 'border-box',
        },
    }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}))

const NestedListItemButton = styled(ListItemButton)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
}))

const translationNavigationItem = {
    path: '/',
    label: 'Translations',
    icon: <Translate/>
}

const adminNavigationItem = {
    path: '/admin',
    label: 'Admin',
    icon: <Person/>
}


const location = window.location.pathname

export default () => {
    const adminSubmenuItems = [
        ...canEditUsers() ? [{
            path: '/admin/users',
            label: 'Users',
            icon: <Group/>
        }] : [],
        ...canEditProjects() ? [{
            path: '/admin/projects',
            label: 'Projects',
            icon: <Work/>
        }] : [],
        ...canEditLanguages() ? [{
            path: '/admin/languages',
            label: 'Languages',
            icon: <Language/>
        }] : []
    ]
    const theme = useTheme()
    const [isDrawerOpen, setDraweOpen] = React.useState(false)

    const iconColor = (isActive: boolean) => isActive ? '#00B482' : 'inherit'

    return (
        <>
            <CssBaseline/>
            <StyledDrawer
                onMouseEnter={() => setDraweOpen(true)}
                onMouseLeave={() => setDraweOpen(false)}
                variant="permanent"
                open={isDrawerOpen}
            >
                <DrawerHeader>
                    <IconButton>
                        {theme.direction === 'rtl' ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                    </IconButton>
                </DrawerHeader>
                <Divider/>
                <List>
                    <ListItemButton 
                        key={1} 
                        onClick={() => window.location.assign(`${translationNavigationItem.path}`)}
                        sx={{
                            minHeight: 48,
                            justifyContent: isDrawerOpen ? 'initial' : 'center',
                            px: 2.5,
                        }}
                    >
                        <ListItemIcon 
                            sx={{ 
                                color: iconColor(location === translationNavigationItem.path || location.includes('editTranslation')),
                                minWidth: 0,
                                mr: isDrawerOpen ? 3 : 'auto',
                                justifyContent: 'center',
                            }}
                        >
                            {translationNavigationItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                            primary={translationNavigationItem.label}
                            sx={{ 
                                opacity: isDrawerOpen ? 1 : 0,
                                color: iconColor(location === translationNavigationItem.path || location.includes('editTranslation'))
                            }}
                        />
                    </ListItemButton>

                    {canSeeAdmin() && <ListItem 
                        key={2}
                        disablePadding
                        sx={{ display: 'block' }}
                    >
                        <ListItemButton
                            sx={{
                                minHeight: 48,
                                justifyContent: isDrawerOpen ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon 
                                sx={{ 
                                    color: iconColor(location.includes(adminNavigationItem.path)),
                                    minWidth: 0,
                                    mr: isDrawerOpen ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                {adminNavigationItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                                primary={adminNavigationItem.label}
                                sx={{ 
                                    opacity: isDrawerOpen ? 1 : 0,
                                    color: iconColor(location.includes(adminNavigationItem.path))
                                }}
                            />
                        </ListItemButton>
                    </ListItem>}

                    <Collapse in={isDrawerOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {adminSubmenuItems.map((item, index) => (
                                <NestedListItemButton 
                                    key={index} 
                                    selected={location === item.path} 
                                    onClick={() => window.location.assign(`${item.path}`)}
                                >
                                    <ListItemIcon sx={{ color: iconColor(location === item.path) }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.label}
                                        sx={{ color: iconColor(location === item.path) }}
                                    />
                                </NestedListItemButton>
                            ))}
                        </List>
                    </Collapse>
                </List>
            </StyledDrawer>
        </>
    )
}
